import type { AgentInputItem } from '../types';
import type { MicrocompactConfig } from './types';

const DEFAULT_MAX_TOOL_RESULT_BYTES = 16 * 1024;
const DEFAULT_KEEP_RECENT_TOOL_RESULTS = 4;
const DEFAULT_REPLACEMENT_TEXT =
  '[older tool output omitted from model context by local microcompact]';

type ResolvedMicrocompactConfig = {
  enabled: boolean;
  maxToolResultBytes: number;
  keepRecentToolResults: number;
  replacementText: string;
};

export function microcompactModelContext(
  items: AgentInputItem[],
  config: MicrocompactConfig | undefined,
): AgentInputItem[] {
  const resolved = resolveMicrocompactConfig(config);
  if (!resolved.enabled) {
    return items;
  }

  const toolResultIndexes = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item?.type === 'function_call_result')
    .map(({ index }) => index);
  const protectedIndexes = new Set(
    resolved.keepRecentToolResults > 0
      ? toolResultIndexes.slice(-resolved.keepRecentToolResults)
      : [],
  );
  let changed = false;
  const next = items.map((item, index) => {
    if (
      protectedIndexes.has(index) ||
      !item ||
      typeof item !== 'object' ||
      item.type !== 'function_call_result'
    ) {
      return item;
    }
    const compactedImageOutput = compactViewImageOutput(
      item,
      resolved.maxToolResultBytes,
    );
    if (compactedImageOutput !== undefined) {
      changed = true;
      return {
        ...item,
        output: compactedImageOutput,
      };
    }
    if (!shouldCompactOutput(item.output, resolved.maxToolResultBytes)) {
      return item;
    }
    changed = true;
    return {
      ...item,
      output: resolved.replacementText,
    };
  });

  return changed ? next : items;
}

function resolveMicrocompactConfig(
  config: MicrocompactConfig | undefined,
): ResolvedMicrocompactConfig {
  if (!config) {
    return {
      enabled: false,
      maxToolResultBytes: DEFAULT_MAX_TOOL_RESULT_BYTES,
      keepRecentToolResults: DEFAULT_KEEP_RECENT_TOOL_RESULTS,
      replacementText: DEFAULT_REPLACEMENT_TEXT,
    };
  }
  if (config === true) {
    return {
      enabled: true,
      maxToolResultBytes: DEFAULT_MAX_TOOL_RESULT_BYTES,
      keepRecentToolResults: DEFAULT_KEEP_RECENT_TOOL_RESULTS,
      replacementText: DEFAULT_REPLACEMENT_TEXT,
    };
  }
  return {
    enabled: config.enabled ?? true,
    maxToolResultBytes:
      config.maxToolResultBytes ?? DEFAULT_MAX_TOOL_RESULT_BYTES,
    keepRecentToolResults:
      config.keepRecentToolResults ?? DEFAULT_KEEP_RECENT_TOOL_RESULTS,
    replacementText: config.replacementText ?? DEFAULT_REPLACEMENT_TEXT,
  };
}

function shouldCompactOutput(output: unknown, maxBytes: number): boolean {
  if (typeof output === 'string') {
    return Buffer.byteLength(output, 'utf8') > maxBytes;
  }
  if (
    output &&
    typeof output === 'object' &&
    'type' in output &&
    (output as { type?: unknown }).type === 'text' &&
    typeof (output as { text?: unknown }).text === 'string'
  ) {
    return (
      Buffer.byteLength((output as unknown as { text: string }).text, 'utf8') >
      maxBytes
    );
  }
  if (Array.isArray(output)) {
    return Buffer.byteLength(JSON.stringify(output), 'utf8') > maxBytes;
  }
  return false;
}

function compactViewImageOutput(
  item: AgentInputItem,
  maxToolResultBytes: number,
): string | undefined {
  if (
    !item ||
    typeof item !== 'object' ||
    item.type !== 'function_call_result'
  ) {
    return undefined;
  }
  const record = item as Record<string, unknown>;
  const metadata = getViewImageMetadata(record);
  if (!metadata) {
    return undefined;
  }
  if (!shouldCompactOutput(record.output, maxToolResultBytes)) {
    return undefined;
  }
  const path = String(metadata.path);
  const mimeType = String(metadata.mimeType);
  const byteSize =
    typeof metadata.byteSize === 'number'
      ? ` ${formatByteSize(metadata.byteSize)}`
      : '';
  return `[older image tool output omitted from model context: ${path} ${mimeType}${byteSize}]`;
}

function getViewImageMetadata(
  item: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (isViewImageMetadata(item.display)) {
    return item.display;
  }
  if (!Array.isArray(item.output)) {
    return undefined;
  }
  for (const part of item.output) {
    if (
      part &&
      typeof part === 'object' &&
      (part as Record<string, unknown>).type === 'input_image' &&
      isViewImageMetadata((part as Record<string, unknown>).providerData)
    ) {
      return (part as Record<string, unknown>).providerData as Record<
        string,
        unknown
      >;
    }
  }
  return undefined;
}

function isViewImageMetadata(value: unknown): value is Record<string, unknown> {
  return (
    !!value &&
    typeof value === 'object' &&
    (value as Record<string, unknown>).kind === 'view_image' &&
    typeof (value as Record<string, unknown>).path === 'string' &&
    typeof (value as Record<string, unknown>).mimeType === 'string'
  );
}

function formatByteSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes}B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)}KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}
